import React from 'react';
import {
  Box, Text, Flex, SimpleGrid, Image, Link, Collapse, useDisclosure,
} from '@chakra-ui/react';
import {
  ShieldAlert, Award, ExternalLink, MessageSquareWarning, Clock, CircleX,
  CircleCheck, TriangleAlert, ChevronDown,
} from 'lucide-react';

// Limites oficiais do MLB por faixa do termômetro (developers.mercadolivre.com.br/pt_br/reputacao-de-vendedores).
// "lider" é o teto para manter a medalha MercadoLíder.
const LIMITES_MLB = {
  claims: { lider: 0.01, green: 0.02, yellow: 0.045, orange: 0.08 },
  delayed_handling_time: { lider: 0.06, green: 0.10, yellow: 0.18, orange: 0.22 },
  cancellations: { lider: 0.005, green: 0.015, yellow: 0.035, orange: 0.04 },
};

// A partir de 80% do limite MercadoLíder a métrica é sinalizada como "perto do limite".
const PERTO_DO_LIMITE = 0.8;

// Estados de cada métrica, do melhor para o pior.
const ESTADOS = {
  ok: { rotulo: 'Dentro do limite', cor: 'green.700', fundo: 'green.50', borda: 'green.200' },
  perto: { rotulo: 'Perto do limite', cor: 'yellow.800', fundo: 'yellow.50', borda: 'yellow.300' },
  acima: { rotulo: 'Acima do limite', cor: 'orange.700', fundo: 'orange.50', borda: 'orange.300' },
  amarela: { rotulo: 'Faixa amarela', cor: 'orange.800', fundo: 'orange.100', borda: 'orange.300' },
  laranja: { rotulo: 'Faixa laranja', cor: 'red.700', fundo: 'red.50', borda: 'red.200' },
  vermelha: { rotulo: 'Faixa vermelha', cor: 'red.800', fundo: 'red.100', borda: 'red.300' },
};

// Termômetro: level_id vem como "5_green"; real_level (conta protegida) vem só com a cor.
const TERMOMETRO = [
  { chave: 'red', nome: 'Vermelho', cor: '#E53E3E' },
  { chave: 'orange', nome: 'Laranja', cor: '#ED8936' },
  { chave: 'yellow', nome: 'Amarelo', cor: '#ECC94B' },
  { chave: 'light_green', nome: 'Verde-claro', cor: '#9AE6B4' },
  { chave: 'green', nome: 'Verde', cor: '#38A169' },
];

const MEDALHAS = { platinum: 'Platinum', gold: 'Gold', silver: 'Silver' };

const METRICAS = [
  { chave: 'claims', titulo: 'Reclamações', Icone: MessageSquareWarning, unidade: ['reclamação', 'reclamações'] },
  { chave: 'delayed_handling_time', titulo: 'Atrasos no envio', Icone: Clock, unidade: ['atraso', 'atrasos'] },
  { chave: 'cancellations', titulo: 'Cancelamentos', Icone: CircleX, unidade: ['cancelamento', 'cancelamentos'] },
];

const pct = (v, casas = 2) => `${((v || 0) * 100).toFixed(casas).replace('.', ',')}%`;
// Limites como 1%, 4,5%, 0,5%: uma casa só quando existe.
const pctLimite = (v) => pct(v, Math.round(v * 1000) % 10 ? 1 : 0);
const num = (v) => (v ?? 0).toLocaleString('pt-BR');
const plural = (n, [um, varios]) => `${num(n)} ${n === 1 ? um : varios}`;
const periodo = (p) => (p ? p.replace('days', 'dias').replace('months', 'meses') : '60 dias');
const tabular = { fontVariantNumeric: 'tabular-nums' };

const nivelDoTermometro = (levelId) => {
  if (!levelId) return null;
  const chave = String(levelId).replace(/^\d_/, '');
  return TERMOMETRO.find((t) => t.chave === chave) || null;
};

const faixaDaTaxa = (rate, limites) => {
  if (rate <= limites.lider) return 0;
  if (rate <= limites.green) return 1;
  if (rate <= limites.yellow) return 2;
  if (rate <= limites.orange) return 3;
  return 4;
};

// Quando a conta está protegida o ML zera value/rate e manda os números reais em "excluded".
const lerMetrica = (m, limites) => {
  const protegida = !!m?.excluded;
  const value = (protegida ? m.excluded.real_value : m?.value) ?? 0;
  const rate = (protegida ? m.excluded.real_rate : m?.rate) ?? 0;
  const faixa = faixaDaTaxa(rate, limites);

  let estado = ['acima', 'amarela', 'laranja', 'vermelha'][faixa - 1];
  if (faixa === 0) estado = rate >= limites.lider * PERTO_DO_LIMITE ? 'perto' : 'ok';

  // Cada métrica tem sua própria base (vendas totais, vendas enviadas...); o ML só manda a taxa,
  // então a base é deduzida de value/rate. Sem ocorrências não dá para deduzir.
  let margem = null;
  if (rate > 0 && value > 0) {
    margem = Math.floor(limites.lider * (value / rate) + 1e-9) - value;
  }

  // Posição do marcador na barra: 5 faixas de largura igual, interpolando dentro da faixa.
  const cortes = [0, limites.lider, limites.green, limites.yellow, limites.orange, limites.orange * 1.25];
  const dentro = Math.min(1, Math.max(0, (rate - cortes[faixa]) / (cortes[faixa + 1] - cortes[faixa])));
  const posicao = Math.min(100, (faixa + dentro) * 20);

  return { protegida, value, rate, faixa, estado, margem, posicao, periodo: periodo(m?.period) };
};

// Resumo da conta para leitura rápida: a pior métrica decide o tom.
const veredito = (lidas) => {
  const acima = lidas.filter((l) => l.metrica.faixa > 0);
  const perto = lidas.filter((l) => l.metrica.estado === 'perto');
  const detalhe = (l) => `${l.def.titulo}: ${pct(l.metrica.rate)} (limite ${pctLimite(l.limites.lider)})`;
  if (acima.length) {
    return { tom: 'risco', titulo: 'Acima do limite MercadoLíder', itens: acima.map(detalhe), Icone: TriangleAlert };
  }
  if (perto.length) {
    return { tom: 'atencao', titulo: 'Perto do limite MercadoLíder', itens: perto.map(detalhe), Icone: TriangleAlert };
  }
  return { tom: 'ok', titulo: 'Reputação saudável', itens: ['Todas as métricas dentro dos limites MercadoLíder'], Icone: CircleCheck };
};

const TONS = {
  ok: { cor: 'green.800', fundo: 'green.50', borda: 'green.200', icone: 'green.600' },
  atencao: { cor: 'yellow.900', fundo: 'yellow.50', borda: 'yellow.300', icone: 'yellow.600' },
  risco: { cor: 'red.800', fundo: 'red.50', borda: 'red.200', icone: 'red.500' },
};

const Pill = ({ children, cor = 'gray.700', fundo = 'gray.100', icone }) => (
  <Flex as="span" align="center" gap={1} px={2.5} py={1} borderRadius="full" bg={fundo} color={cor} fontSize="xs" fontWeight="semibold" whiteSpace="nowrap" lineHeight="1.2">
    {icone}
    {children}
  </Flex>
);

const Painel = (props) => <Box p={4} bg="white" borderWidth={1} borderColor="gray.200" borderRadius="lg" minW={0} {...props} />;

const Termometro = ({ nivel, nivelReal }) => (
  <Flex gap={1} w="100%" maxW="200px" h="10px" align="center" aria-hidden="true">
    {TERMOMETRO.map((t) => {
      const ativo = nivel?.chave === t.chave;
      const real = nivelReal && nivelReal.chave === t.chave && nivelReal.chave !== nivel?.chave;
      return (
        <Box
          key={t.chave}
          flex="1"
          h={ativo ? '10px' : '6px'}
          borderRadius="full"
          bg={t.cor}
          opacity={ativo || real ? 1 : 0.22}
          outline={real ? '2px dashed' : 'none'}
          outlineColor="gray.700"
          outlineOffset="1px"
        />
      );
    })}
  </Flex>
);

const CartaoMetrica = ({ def, metrica, limites }) => {
  const { Icone } = def;
  const estado = ESTADOS[metrica.estado];

  let rodape;
  if (metrica.value === 0) {
    rodape = 'Nenhuma ocorrência no período';
  } else if (metrica.margem === null) {
    rodape = 'Taxa arredondada para 0% pelo Mercado Livre';
  } else if (metrica.margem > 0) {
    rodape = `Margem: ~${plural(metrica.margem, def.unidade)} até o limite`;
  } else if (metrica.margem === 0) {
    rodape = 'No limite: a próxima ocorrência ultrapassa';
  } else {
    rodape = `~${plural(Math.abs(metrica.margem), def.unidade)} acima do limite`;
  }

  return (
    <Painel borderColor={metrica.faixa > 0 ? estado.borda : 'gray.200'}>
      <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={3}>
        <Flex align="center" gap={2} color="gray.600">
          <Icone size={16} aria-hidden="true" />
          <Text fontSize="sm" fontWeight="semibold">{def.titulo}</Text>
        </Flex>
        <Pill cor={estado.cor} fundo={estado.fundo}>{estado.rotulo}</Pill>
      </Flex>

      <Flex align="baseline" gap={2} wrap="wrap">
        <Text fontSize="3xl" fontWeight="bold" lineHeight="1" color="gray.800" sx={tabular}>{pct(metrica.rate)}</Text>
        <Text fontSize="sm" color="gray.500">{plural(metrica.value, def.unidade)}</Text>
      </Flex>

      {/* Escala por faixa do termômetro, com o marcador na taxa atual */}
      <Box
        position="relative" mt={4} mb={1}
        role="img"
        aria-label={`${def.titulo}: ${pct(metrica.rate)}, limite MercadoLíder ${pctLimite(limites.lider)}`}
      >
        <Flex h="6px" borderRadius="full" overflow="hidden" gap="2px">
          {['green.500', 'green.300', 'yellow.400', 'orange.400', 'red.400'].map((c) => (
            <Box key={c} flex="1" bg={c} />
          ))}
        </Flex>
        <Box
          position="absolute" top="-4px" left={`calc(${metrica.posicao}% - 7px)`}
          w="14px" h="14px" borderRadius="full" bg="white" borderWidth="3px" borderColor="gray.800"
        />
      </Box>
      <Box position="relative" h="14px" fontSize="10px" color="gray.500" sx={tabular} aria-hidden="true">
        {[limites.lider, limites.green, limites.yellow, limites.orange].map((corte, i) => (
          <Text key={corte} position="absolute" left={`${(i + 1) * 20}%`} transform="translateX(-50%)" fontWeight={i === 0 ? 'bold' : 'normal'} color={i === 0 ? 'gray.700' : undefined}>
            {pctLimite(corte)}
          </Text>
        ))}
      </Box>

      <Text mt={3} fontSize="xs" fontWeight="medium" color={metrica.margem !== null && metrica.margem < 0 ? 'red.600' : 'gray.600'}>
        {rodape}
      </Text>
      <Text fontSize="11px" color="gray.500">
        Limite MercadoLíder {pctLimite(limites.lider)} · últimos {metrica.periodo}
      </Text>
      {metrica.protegida && (
        <Text mt={2} fontSize="11px" color="orange.700" fontWeight="medium">No Mercado Livre aparece 0% (protegida)</Text>
      )}
    </Painel>
  );
};

const SellerReputation = ({ sellerData, conta }) => {
  const dados = useDisclosure();
  const {
    seller_reputation = {},
    company = {},
    nickname = '',
    registration_date = '',
    email = '',
    identification = {},
    permalink = '',
  } = sellerData || {};

  const {
    level_id, power_seller_status, real_level, protection_end_date,
    transactions = {}, metrics = {},
  } = seller_reputation || {};
  const { ratings = {} } = transactions;
  const { corporate_name = '' } = company || {};

  const nivel = nivelDoTermometro(level_id);
  const nivelReal = real_level ? nivelDoTermometro(real_level) : null;
  const medalha = MEDALHAS[power_seller_status];
  const vendasPeriodo = metrics.sales?.completed ?? 0;

  const lidas = METRICAS.map((def) => ({
    def,
    limites: LIMITES_MLB[def.chave],
    metrica: lerMetrica(metrics[def.chave], LIMITES_MLB[def.chave]),
  }));
  const resumo = veredito(lidas);
  const tom = TONS[resumo.tom];

  // Proteção: dias corridos até a data final (ignora o horário).
  const fimProtecao = protection_end_date ? new Date(protection_end_date) : null;
  const protegida = !!fimProtecao || lidas.some((l) => l.metrica.protegida);
  let diasProtecao = null;
  if (fimProtecao) {
    const hoje = new Date();
    const diaFim = new Date(fimProtecao.getFullYear(), fimProtecao.getMonth(), fimProtecao.getDate());
    const diaHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    diasProtecao = Math.max(0, Math.round((diaFim - diaHoje) / 86400000));
  }

  const canceladasPct = transactions.total ? transactions.canceled / transactions.total : 0;

  return (
    <Box
      as="section"
      aria-label={`Reputação Mercado Livre, conta ${conta}`}
      p={{ base: 3, md: 5 }}
      borderWidth={1} borderColor="gray.200" borderRadius="xl" bg="gray.50" boxShadow="sm"
      maxW="1200px" mx="auto"
    >
      {/* Cabeçalho */}
      <Flex gap={3} align="center" wrap="wrap" mb={4}>
        <Image
          src="/ml_logo_2.webp" alt="Mercado Livre" flexShrink={0}
          w={{ base: '84px', md: '96px' }} h={{ base: '32px', md: '36px' }}
          borderRadius="full" border="1px solid black"
        />
        <Box minW={0} flex="1 1 140px">
          <Text fontSize="11px" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
            Conta {conta}
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="gray.800" noOfLines={1}>{nickname || corporate_name || '—'}</Text>
        </Box>
        <Pill cor={medalha ? 'yellow.900' : 'gray.600'} fundo={medalha ? 'yellow.100' : 'gray.100'} icone={<Award size={14} aria-hidden="true" />}>
          {medalha ? `MercadoLíder ${medalha}` : 'Sem MercadoLíder'}
        </Pill>
      </Flex>

      {/* Veredito + termômetro: o que se lê primeiro */}
      <Flex
        p={4} mb={3} gap={4} wrap="wrap" align="center" justify="space-between"
        bg={tom.fundo} borderWidth={1} borderColor={tom.borda} borderRadius="lg" color={tom.cor}
      >
        <Flex gap={3} align="start" flex="1 1 240px" minW={0}>
          <Box color={tom.icone} mt="2px" flexShrink={0}><resumo.Icone size={22} aria-hidden="true" /></Box>
          <Box minW={0}>
            <Text fontWeight="bold" fontSize="md">{resumo.titulo}</Text>
            {resumo.itens.map((item) => (
              <Text key={item} fontSize="sm" sx={tabular}>{item}</Text>
            ))}
          </Box>
        </Flex>
        <Box flex="0 1 200px" minW="150px">
          <Text fontSize="11px" fontWeight="semibold" mb={1.5} opacity={0.8}>Termômetro</Text>
          <Termometro nivel={nivel} nivelReal={nivelReal} />
          <Text mt={1.5} fontSize="sm" fontWeight="bold">
            {nivel ? nivel.nome : 'Sem reputação ainda'}
            {nivelReal && nivelReal.chave !== nivel?.chave && (
              <Text as="span" fontWeight="medium"> · real: {nivelReal.nome}</Text>
            )}
          </Text>
        </Box>
      </Flex>

      {/* Conta protegida: o ML zera as métricas públicas e manda as reais em "excluded" */}
      {protegida && (
        <Flex gap={3} p={4} mb={3} bg="white" borderWidth={1} borderColor="orange.300" borderLeftWidth="4px" borderRadius="lg" color="gray.700">
          <Box color="orange.500" flexShrink={0} mt="2px"><ShieldAlert size={20} aria-hidden="true" /></Box>
          <Box fontSize="sm" minW={0}>
            <Text fontWeight="bold" color="gray.800">
              Reputação protegida
              {fimProtecao && ` até ${fimProtecao.toLocaleDateString('pt-BR')}`}
              {diasProtecao !== null && (diasProtecao === 0 ? ' · termina hoje' : ` · faltam ${plural(diasProtecao, ['dia', 'dias'])}`)}
            </Text>
            <Text mt={0.5}>
              O Mercado Livre mostra as métricas zeradas enquanto durar a proteção. Os números abaixo são os
              reais e passam a contar quando ela terminar.
            </Text>
          </Box>
        </Flex>
      )}

      {/* Métricas */}
      <Flex justify="space-between" align="baseline" gap={2} wrap="wrap" mt={5} mb={2}>
        <Text fontWeight="bold" color="gray.800">Métricas da reputação</Text>
        <Text fontSize="xs" color="gray.500" sx={tabular}>
          {num(vendasPeriodo)} vendas concluídas · últimos {periodo(metrics.sales?.period)}
        </Text>
      </Flex>
      {/* minChildWidth: a grade se ajusta à largura do card, não da tela (o card aparece em 45%, 50% ou 100%) */}
      <SimpleGrid minChildWidth="175px" spacing={3}>
        {lidas.map(({ def, limites, metrica }) => (
          <CartaoMetrica key={def.chave} def={def} limites={limites} metrica={metrica} />
        ))}
      </SimpleGrid>

      {/* Histórico */}
      <Text fontWeight="bold" color="gray.800" mt={5} mb={2}>Desde o início da conta</Text>
      <SimpleGrid minChildWidth="240px" spacing={3}>
        <Painel>
          <Text fontSize="xs" color="gray.500" mb={3}>Vendas</Text>
          <SimpleGrid columns={3} spacing={2} sx={tabular}>
            <Box minW={0}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">{num(transactions.total)}</Text>
              <Text fontSize="xs" color="gray.500">Total</Text>
            </Box>
            <Box minW={0}>
              <Text fontSize="lg" fontWeight="bold" color="green.600">{num(transactions.completed)}</Text>
              <Text fontSize="xs" color="gray.500">Concluídas</Text>
            </Box>
            <Box minW={0}>
              <Text fontSize="lg" fontWeight="bold" color="red.500">{num(transactions.canceled)}</Text>
              <Text fontSize="xs" color="gray.500">Canceladas · {pct(canceladasPct, 0)}</Text>
            </Box>
          </SimpleGrid>
        </Painel>

        <Painel>
          <Text fontSize="xs" color="gray.500" mb={3}>Avaliações dos compradores</Text>
          <Flex h="10px" borderRadius="full" overflow="hidden" bg="gray.100" gap="2px" aria-hidden="true">
            <Box w={`${(ratings.positive || 0) * 100}%`} bg="green.400" />
            <Box w={`${(ratings.neutral || 0) * 100}%`} bg="gray.400" />
            <Box w={`${(ratings.negative || 0) * 100}%`} bg="red.400" />
          </Flex>
          <Flex mt={3} gap={4} fontSize="xs" color="gray.600" wrap="wrap" sx={tabular}>
            <Text><Text as="span" color="green.600" fontWeight="bold" fontSize="md">{pct(ratings.positive, 0)}</Text> positivas</Text>
            <Text><Text as="span" color="gray.600" fontWeight="bold" fontSize="md">{pct(ratings.neutral, 0)}</Text> neutras</Text>
            <Text><Text as="span" color="red.500" fontWeight="bold" fontSize="md">{pct(ratings.negative, 0)}</Text> negativas</Text>
          </Flex>
        </Painel>
      </SimpleGrid>

      {/* Dados cadastrais: recolhidos para não competir com a reputação */}
      <Box mt={3} bg="white" borderWidth={1} borderColor="gray.200" borderRadius="lg">
        <Flex
          as="button" type="button" onClick={dados.onToggle} aria-expanded={dados.isOpen}
          w="100%" px={4} py={3} justify="space-between" align="center" gap={2}
          fontSize="sm" fontWeight="semibold" color="gray.700" borderRadius="lg"
          _hover={{ bg: 'gray.50' }} _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
        >
          Dados da conta
          <Box as="span" transform={dados.isOpen ? 'rotate(180deg)' : 'none'} transition="transform .2s">
            <ChevronDown size={16} aria-hidden="true" />
          </Box>
        </Flex>
        <Collapse in={dados.isOpen} animateOpacity>
          <Box px={4} pb={4} fontSize="xs">
            <SimpleGrid minChildWidth="200px" spacingX={4} spacingY={3}>
              <Box minW={0}>
                <Text color="gray.500">Razão social</Text>
                <Text fontWeight="semibold" wordBreak="break-word">{corporate_name || '—'}</Text>
              </Box>
              <Box minW={0}>
                <Text color="gray.500">E-mail</Text>
                <Text fontWeight="semibold" wordBreak="break-word">{email || '—'}</Text>
              </Box>
              <Box>
                <Text color="gray.500">CNPJ</Text>
                <Text fontWeight="semibold" sx={tabular}>{identification?.number || '—'}</Text>
              </Box>
              <Box>
                <Text color="gray.500">Conta criada em</Text>
                <Text fontWeight="semibold">{registration_date ? new Date(registration_date).toLocaleDateString('pt-BR') : '—'}</Text>
              </Box>
            </SimpleGrid>
            {permalink && (
              <Link href={permalink} isExternal mt={3} display="inline-flex" alignItems="center" gap={1} color="blue.600" fontWeight="semibold">
                Ver perfil no Mercado Livre <ExternalLink size={12} aria-hidden="true" />
              </Link>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default SellerReputation;
